import React, { useMemo, useState } from "react";
import { Modal, Form, Input, Select, Row, Col } from "antd";

const AddressFormModal = ({
  visible,
  onCancel,
  onSave,
  currentPerson,
  editingAddress,
  geoRawData,
}) => {
  const [form] = Form.useForm();
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedSubDistrict, setSelectedSubDistrict] = useState(null);

  // Reset form เมื่อเปิด Modal
  React.useEffect(() => {
    if (visible) {
      if (editingAddress) {
        // ✅ หา provCode จาก provDesc (PROVDES)
        const province = geoRawData?.province?.find(
          (p) => p.provDesc === editingAddress.PROVDES
        );
        const provinceCode = province ? String(province.provCode) : null;

        // ✅ หา distCode จาก distDesc (AUMPDES)
        const district = geoRawData?.district?.find(
          (d) =>
            d.distDesc === editingAddress.AUMPDES &&
            String(d.provCode) === provinceCode
        );
        const districtCode = district ? String(district.distCode) : null;

        // ✅ หา subDistCode จาก subDistDesc (TUMB)
        const subDistrict = geoRawData?.subDistrict?.find(
          (s) =>
            s.subDistDesc === editingAddress.TUMB &&
            String(s.distCode) === districtCode &&
            String(s.provCode) === provinceCode
        );
        const subDistrictCode = subDistrict
          ? String(subDistrict.subDistCode)
          : null;

        console.log("Edit Mode - Codes:", {
          provinceCode,
          districtCode,
          subDistrictCode,
        });

        // Set state
        setSelectedProvince(provinceCode);
        setSelectedDistrict(districtCode);
        setSelectedSubDistrict(subDistrictCode);

        // Set form values
        form.setFieldsValue({
          ADDRNO: editingAddress.ADDRNO,
          ADDR1: editingAddress.ADDR1,
          ADDR2: editingAddress.ADDR2 || null,
          PROVDES: provinceCode,
          AUMPDES: districtCode,
          TUMB: subDistrictCode,
          ZIP: editingAddress.ZIP,
        });
      } else {
        // Reset ทุกอย่างเมื่อเป็น add mode
        form.resetFields();
        form.setFieldsValue({ ADDRNO: 4 });
        setSelectedProvince(null);
        setSelectedDistrict(null);
        setSelectedSubDistrict(null);
      }
    }
  }, [visible, editingAddress, form, geoRawData]);

  // ✅ จังหวัดทั้งหมด
  const provinceOptions = useMemo(() => {
    if (!geoRawData?.province) return [];
    return geoRawData.province.map((prov) => ({
      label: prov.provDesc,
      value: String(prov.provCode),
      data: prov,
    }));
  }, [geoRawData]);

  // ✅ อำเภอ - filter ตามจังหวัดที่เลือกเท่านั้น
  const districtOptions = useMemo(() => {
    if (!geoRawData?.district || !selectedProvince) return [];

    const filteredDistricts = geoRawData.district.filter(
      (dist) => String(dist.provCode) === String(selectedProvince)
    );

    return filteredDistricts.map((dist, index) => ({
      label: dist.distDesc,
      value: String(dist.distCode),
      data: {
        ...dist,
        provCode: String(dist.provCode),
        distCode: String(dist.distCode),
      },
      key: `dist-${dist.provCode}-${dist.distCode}-${index}`,
    }));
  }, [geoRawData, selectedProvince]);

  // ✅ ตำบล - filter ตามอำเภอที่เลือกเท่านั้น
  const subDistrictOptions = useMemo(() => {
    if (!geoRawData?.subDistrict || !selectedDistrict) return [];

    const filteredSubDistricts = geoRawData.subDistrict.filter(
      (sub) =>
        String(sub.provCode) === String(selectedProvince) &&
        String(sub.distCode) === String(selectedDistrict)
    );

    return filteredSubDistricts.map((sub, index) => ({
      label: sub.subDistDesc,
      value: String(sub.subDistCode),
      data: {
        ...sub,
        provCode: String(sub.provCode),
        distCode: String(sub.distCode),
        subDistCode: String(sub.subDistCode),
      },
      key: `sub-${sub.provCode}-${sub.distCode}-${sub.subDistCode}-${index}`,
    }));
  }, [geoRawData, selectedProvince, selectedDistrict]);

  // ✅ Handler: เลือกจังหวัด
  const handleProvinceChange = (value) => {
    setSelectedProvince(value);
    setSelectedDistrict(null);
    setSelectedSubDistrict(null);

    // Reset อำเภอ, ตำบล และรหัสไปรษณีย์
    form.setFieldsValue({
      AUMPDES: undefined,
      TUMB: undefined,
      ZIP: "",
    });
  };

  // ✅ Handler: เลือกอำเภอ
  const handleDistrictChange = (value) => {
    setSelectedDistrict(value);
    setSelectedSubDistrict(null);

    // Reset ตำบลและรหัสไปรษณีย์
    form.setFieldsValue({
      TUMB: undefined,
      ZIP: "",
    });
  };

  // ✅ Handler: เลือกตำบล - Auto-fill รหัสไปรษณีย์
  const handleSubDistrictChange = (value, option) => {
    if (!option) {
      setSelectedSubDistrict(null);
      form.setFieldsValue({ ZIP: "" });
      return;
    }

    setSelectedSubDistrict(value);

    // ✅ Auto-fill รหัสไปรษณีย์
    if (option?.data?.zipcode) {
      form.setFieldsValue({
        ZIP: option.data.zipcode,
      });
    }
  };

  // ✅ บันทึกข้อมูล
  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // หาชื่อเต็มจาก code
      const provinceName =
        provinceOptions.find((p) => p.value === values.PROVDES)?.label || "";
      const districtName =
        districtOptions.find((d) => d.value === values.AUMPDES)?.label || "";
      const subDistrictName =
        subDistrictOptions.find((s) => s.value === values.TUMB)?.label || "";

      const addressData = {
        ...values,
        PROVDES: provinceName,
        AUMPDES: districtName,
        TUMB: subDistrictName,
        TELP: currentPerson?.ADDRESS[0]?.TELP,
      };

      console.log("Saving address:", addressData);
      onSave(addressData);
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const fullName = (p) =>
    p ? [p?.SNAM, p?.NAME1, p?.NAME2].filter(Boolean).join(" ") : "";

  return (
    <Modal
      title={
        <span style={{ fontSize: 17 }}>
          {editingAddress
            ? `แก้ไขที่อยู่ - ${fullName(currentPerson)}`
            : `เพิ่มที่อยู่ใหม่ - ${fullName(currentPerson)}`}
        </span>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleSave}
      width={900}
      okText={<span style={{ fontSize: 15 }}>บันทึก</span>}
      cancelText={<span style={{ fontSize: 15 }}>ยกเลิก</span>}
    >
      <Form form={form} layout="vertical" initialValues={{ ADDRNO: 4 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="ADDRNO"
              label={<span style={{ fontSize: 14 }}>ประเภทที่อยู่</span>}
              rules={[{ required: true, message: "กรุณาเลือกประเภทที่อยู่" }]}
            >
              <Select placeholder="เลือกประเภทที่อยู่" size="large">
                <Select.Option value={1}>ทะเบียนบ้าน</Select.Option>
                <Select.Option value={2}>ส่งเอกสาร</Select.Option>
                <Select.Option value={3}>ที่ทำงาน</Select.Option>
                <Select.Option value={4}>ทะเบียนราษฎร</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="ADDR1"
          label={<span style={{ fontSize: 14 }}>ที่อยู่ บรรทัดที่ 1</span>}
          rules={[{ required: true, message: "กรุณากรอกที่อยู่" }]}
        >
          <Input placeholder="เลขที่, หมู่, ซอย" size="large" />
        </Form.Item>

        <Form.Item
          name="ADDR2"
          label={<span style={{ fontSize: 14 }}>ที่อยู่ บรรทัดที่ 2</span>}
        >
          <Input placeholder="ถนน, หมู่บ้าน" size="large" />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="PROVDES"
              label={<span style={{ fontSize: 14 }}>จังหวัด</span>}
              rules={[{ required: true, message: "กรุณาเลือกจังหวัด" }]}
            >
              <Select
                showSearch
                placeholder="เลือกจังหวัด"
                optionFilterProp="label"
                options={provinceOptions}
                onChange={handleProvinceChange}
                allowClear
                loading={!geoRawData}
                disabled={!geoRawData}
                size="large"
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="AUMPDES"
              label={<span style={{ fontSize: 14 }}>อำเภอ/เขต</span>}
              rules={[{ required: true, message: "กรุณาเลือกอำเภอ" }]}
            >
              <Select
                showSearch
                placeholder={
                  selectedProvince ? "เลือกอำเภอ/เขต" : "กรุณาเลือกจังหวัดก่อน"
                }
                optionFilterProp="label"
                options={districtOptions}
                onChange={handleDistrictChange}
                allowClear
                disabled={!selectedProvince}
                size="large"
              />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="TUMB"
              label={<span style={{ fontSize: 14 }}>ตำบล/แขวง</span>}
              rules={[{ required: true, message: "กรุณาเลือกตำบล" }]}
            >
              <Select
                showSearch
                placeholder={
                  selectedDistrict ? "เลือกตำบล/แขวง" : "กรุณาเลือกอำเภอก่อน"
                }
                optionFilterProp="label"
                options={subDistrictOptions}
                onChange={handleSubDistrictChange}
                allowClear
                disabled={!selectedDistrict}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="ZIP"
              label={<span style={{ fontSize: 14 }}>รหัสไปรษณีย์</span>}
              rules={[
                { required: true, message: "กรุณากรอกรหัสไปรษณีย์" },
                {
                  pattern: /^[0-9]{5}$/,
                  message: "รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก",
                },
              ]}
            >
              <Input
                placeholder="รหัสไปรษณีย์ 5 หลัก"
                maxLength={5}
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default AddressFormModal;
